/**
 * CORE-BK responsibility 6 / 9 — the retention registry, shown as it is (Core-100 R4 W4).
 *
 * `data_retention_policies` had no admin surface (RPC only), so the classes catalogued for deletion — and the fact
 * that every period is still UNSET (OD-01: Privacy Policy §6 states no age-based period for them) — were invisible.
 * This is an OBSERVER like the posture page around it: no "set period", no "enable". A period is set only through
 * `set_data_retention_policy` with a stated legal basis.
 *
 * A failed read says so; it never renders as an empty registry.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface RetentionClassRow {
  data_class: string;
  description: string;
  source_store: string;
  age_basis: string;
  retention_days: number | null;
  enabled: boolean;
  legal_basis: string | null;
}

/** The period, in words: an unset period is a pending decision, never "keep forever". */
export function retentionPeriodText(r: Pick<RetentionClassRow, 'retention_days' | 'enabled' | 'legal_basis'>): string {
  if (r.retention_days == null) return 'Period unset — awaiting a legal decision (nothing is deleted)';
  const days = `${r.retention_days} day${r.retention_days === 1 ? '' : 's'}`;
  if (!r.enabled) return `${days} — set, not enabled (nothing is deleted)`;
  return `${days} — enabled${r.legal_basis ? ` (basis: ${r.legal_basis})` : ''}`;
}

type Rpc = { from: (t: string) => { select: (c: string) => { order: (c: string) => PromiseLike<{ data: unknown; error: { message?: string } | null }> } } };

export function RetentionRegistryList() {
  const [rows, setRows] = useState<RetentionClassRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data, error } = await (supabase as unknown as Rpc)
          .from('data_retention_policies')
          .select('data_class, description, source_store, age_basis, retention_days, enabled, legal_basis')
          .order('data_class');
        if (!alive) return;
        if (error || !Array.isArray(data)) { setFailed(true); setRows(null); return; }
        setRows(data as RetentionClassRow[]);
      } catch {
        if (alive) { setFailed(true); setRows(null); }
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section className="space-y-2" data-testid="retention-registry">
      <h2 className="text-base font-semibold">Retention classes</h2>
      <p className="text-xs text-muted-foreground">
        What the age-based registry would delete, store by store. Legal holds always win. Read-only here.
      </p>
      {failed && (
        <p className="text-sm text-red-700 dark:text-red-400" data-testid="retention-registry-error">
          Could not read the retention registry — this is an error, not an empty registry.
        </p>
      )}
      {!failed && rows === null && <p className="text-sm text-muted-foreground">Loading retention classes…</p>}
      {rows && (
        <ul className="divide-y rounded-md border text-sm">
          {rows.map((r) => (
            <li key={r.data_class} className="p-3" data-testid="retention-class" data-class={r.data_class}
                data-period-set={r.retention_days != null ? 'true' : 'false'}>
              <div className="font-medium">{r.description}</div>
              <div className="text-xs text-muted-foreground">
                <span className="font-mono">{r.data_class}</span> · store <span className="font-mono">{r.source_store}</span> · age from {r.age_basis}
              </div>
              <div className="text-xs mt-1">{retentionPeriodText(r)}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
